import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Input } from 'reactstrap';
import { injectIntl, intlShape } from 'react-intl';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import { roundWithPrecision } from '../../../util/number';
import Icon from '../../common/Icon';
import useProductMatrixCell from './useProductMatrixCell';

const ProductMatrixCell = ({
  accountStore,
  configStore,
  uiStore,
  intl,
  cellCount,
  onHandleProductQuantity,
  onRenderTooltip,
  className,
  product,
  propertyListRef,
  value: controlledValue,
}) => {
  const {
    blockedState,
    cellStyle,
    closePopover,
    displayQuantity,
    handleProductQuantity,
    inputRef,
    isOpen,
    openPopover,
  } = useProductMatrixCell({
    accountStore,
    configStore,
    uiStore,
    cellCount,
    onHandleProductQuantity,
    product,
    propertyListRef,
    value: controlledValue,
  });

  const getUnavailableProduct = () => (
    <div
      key={uniqueId('Cell-')}
      className="ProductMatrixCell__unavailable-product"
    >
      <div className="ProductMatrixCell__line" />
    </div>
  );

  const getNoProduct = () => {
    return (
      <div
        key={uniqueId('Cell-')}
        className="ProductMatrixCell__no-product"
        style={cellStyle}
      >
        <Icon name="ban" />
      </div>
    );
  };

  const renderPackageInput = () => {
    const getPackageSizeSum = () => {
      const sum = roundWithPrecision(
        Number(displayQuantity || 0) * product.package_size,
        3
      );

      return (
        <div className="ProductMatrixCell__package-size-sum">
          ( {sum}
          <span className="ProductMatrixCell__package-stock-unit">
            {product.stock_unit}
          </span>
          )
        </div>
      );
    };

    return (
      <div className="ProductMatrixCell__package-input">
        <Input
          id={`ProductInfo-${product.id}`}
          className="ProductMatrixCell__input"
          type="number"
          name={product.id}
          value={displayQuantity}
          onFocus={openPopover}
          onBlur={closePopover}
          onChange={handleProductQuantity}
          innerRef={inputRef}
          aria-label={intl.formatMessage(globalTranslations.quantityTitle)}
          placeholder={product.package_unit_name}
          pattern="[0-9]*"
        />
        {getPackageSizeSum()}
      </div>
    );
  };

  const renderInput = () => {
    if (product.sellInPackage) {
      return renderPackageInput();
    }

    return (
      <Input
        id={`ProductInfo-${product.id}`}
        className="ProductMatrixCell__input"
        type="number"
        name={product.id}
        value={displayQuantity}
        onFocus={openPopover}
        onBlur={closePopover}
        onChange={handleProductQuantity}
        innerRef={inputRef}
        aria-label={intl.formatMessage(globalTranslations.quantityTitle)}
        placeholder={product.stock_unit}
      />
    );
  };

  if (blockedState === 'missing') {
    return getNoProduct();
  }

  if (blockedState === 'unavailable') {
    return getUnavailableProduct();
  }

  return (
    <div
      className={classNames('ProductMatrixCell', className)}
      style={cellStyle}
    >
      {renderInput()}
      {onRenderTooltip(isOpen)}
    </div>
  );
};

ProductMatrixCell.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  intl: intlShape.isRequired,
  cellCount: PropTypes.number.isRequired,
  onHandleProductQuantity: PropTypes.func.isRequired,
  onRenderTooltip: PropTypes.func.isRequired,
  className: PropTypes.string,
  product: PropTypes.object,
  propertyListRef: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default injectIntl(
  inject('accountStore', 'configStore', 'uiStore')(observer(ProductMatrixCell))
);
